/**
 * NodeDetailPopover — Read-only detail panel for spectator mode.
 * Tap a node on mobile to see activity/exchange details.
 */
import { useState, useCallback } from 'react'
import {
  X,
  Box,
  MapPin,
  ArrowDownToLine,
  ArrowUpFromLine,
  Package,
  Link2,
  Hash,
  Copy,
  Check,
  ExternalLink,
  Database,
  Clock,
  Layers,
  Tag,
} from 'lucide-react'
import type { ActivityWithExchanges, ProjectExchange } from '@/lib/types/project-graph'

interface NodeDetailPopoverProps {
  nodeId: string
  nodeType: 'activity' | 'exchange'
  activity?: ActivityWithExchanges
  exchange?: ProjectExchange
  exchangeType?: string
  parentActivity?: ActivityWithExchanges
  onClose: () => void
}

const exchangeIcons: Record<string, typeof ArrowDownToLine> = {
  technosphere: ArrowDownToLine,
  biosphere: ArrowUpFromLine,
  production: Package,
  'cross-project': Link2,
}

const exchangeColors: Record<string, string> = {
  technosphere: 'text-blue-400',
  biosphere: 'text-green-400',
  production: 'text-amber-400',
  'cross-project': 'text-pink-400',
}

const exchangeBgColors: Record<string, string> = {
  technosphere: 'bg-blue-500/10 border-blue-500/20',
  biosphere: 'bg-green-500/10 border-green-500/20',
  production: 'bg-amber-500/10 border-amber-500/20',
  'cross-project': 'bg-pink-500/10 border-pink-500/20',
}

function formatAmount(amount: number): string {
  if (Math.abs(amount) >= 1000)
    return amount.toLocaleString(undefined, { maximumFractionDigits: 0 })
  if (Math.abs(amount) >= 1) return amount.toLocaleString(undefined, { maximumFractionDigits: 2 })
  if (Math.abs(amount) >= 0.001)
    return amount.toLocaleString(undefined, { maximumFractionDigits: 4 })
  return amount.toExponential(2)
}

/** Clipboard copy helper with feedback */
function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = useCallback(() => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      })
      .catch(() => {})
  }, [text])

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 text-slate-400 hover:text-slate-200 transition-colors"
      title={label || 'Copy to clipboard'}
    >
      {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
    </button>
  )
}

/** Detail row with label and value */
function DetailRow({
  icon: Icon,
  label,
  value,
  mono,
  copyable,
}: {
  icon: typeof Box
  label: string
  value: string
  mono?: boolean
  copyable?: boolean
}) {
  return (
    <div className="flex items-start gap-2 py-1">
      <Icon className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</p>
        <div className="flex items-center gap-1.5">
          <p className={`text-xs text-slate-200 break-all ${mono ? 'font-mono text-[11px]' : ''}`}>
            {value}
          </p>
          {copyable && <CopyButton text={value} label={`Copy ${label}`} />}
        </div>
      </div>
    </div>
  )
}

export function NodeDetailPopover({
  nodeType,
  activity,
  exchange,
  exchangeType,
  parentActivity,
  onClose,
}: NodeDetailPopoverProps) {
  return (
    <div className="absolute bottom-0 left-0 right-0 z-30 animate-in slide-in-from-bottom duration-200">
      <div className="bg-slate-900/95 backdrop-blur-md border-t border-slate-700 rounded-t-xl max-h-[50vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full hover:bg-slate-800 text-slate-400 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {nodeType === 'activity' && activity && <ActivityDetail activity={activity} />}

        {nodeType === 'exchange' && exchange && (
          <ExchangeDetail
            exchange={exchange}
            exchangeType={exchangeType || 'technosphere'}
            parentActivity={parentActivity}
          />
        )}
      </div>
    </div>
  )
}

function ActivityDetail({ activity }: { activity: ActivityWithExchanges }) {
  const techno = activity.exchanges.filter((e) => e.exchange_type === 'technosphere')
  const bio = activity.exchanges.filter((e) => e.exchange_type === 'biosphere')
  const prod = activity.exchanges.filter((e) => e.exchange_type === 'production')

  return (
    <div className="p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-indigo-500/20 flex items-center justify-center">
          <Box className="w-4 h-4 text-indigo-400" />
        </div>
        <span className="text-xs font-medium text-indigo-400 uppercase tracking-wide">
          Activity
        </span>
      </div>

      {/* Name */}
      <h3 className="text-sm font-semibold text-white leading-snug pr-8">{activity.name}</h3>

      {/* Key properties */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
        <DetailRow icon={MapPin} label="Location" value={activity.location} />
        <DetailRow icon={Package} label="Unit" value={activity.unit} />
        {activity.reference_product && (
          <DetailRow icon={Tag} label="Reference Product" value={activity.reference_product} />
        )}
      </div>

      {/* Activity code */}
      <div className="pt-1 border-t border-slate-800">
        <DetailRow icon={Hash} label="Activity Code" value={activity.code} mono copyable />
      </div>

      {/* Exchange breakdown */}
      <div className="pt-1 border-t border-slate-800">
        <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">
          Exchange Breakdown
        </p>
        <div className="flex flex-wrap gap-2">
          {techno.length > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-500/10 border border-blue-500/20 text-xs text-blue-300">
              <ArrowDownToLine className="w-3 h-3" />
              {techno.length} input{techno.length !== 1 ? 's' : ''}
            </span>
          )}
          {bio.length > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-green-500/10 border border-green-500/20 text-xs text-green-300">
              <ArrowUpFromLine className="w-3 h-3" />
              {bio.length} emission{bio.length !== 1 ? 's' : ''}
            </span>
          )}
          {prod.length > 0 && (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-500/10 border border-amber-500/20 text-xs text-amber-300">
              <Package className="w-3 h-3" />
              {prod.length} output{prod.length !== 1 ? 's' : ''}
            </span>
          )}
          {activity.exchanges.length === 0 && (
            <span className="text-xs text-slate-500">No exchanges yet</span>
          )}
        </div>
      </div>

      {/* Timestamp */}
      {activity.created_at && (
        <div className="flex items-center gap-1.5 text-[10px] text-slate-600">
          <Clock className="w-3 h-3" />
          Created{' '}
          {new Date(activity.created_at).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      )}
    </div>
  )
}

function ExchangeDetail({
  exchange,
  exchangeType,
  parentActivity,
}: {
  exchange: ProjectExchange
  exchangeType: string
  parentActivity?: ActivityWithExchanges
}) {
  const config = {
    icon: exchangeIcons[exchangeType] || ArrowDownToLine,
    color: exchangeColors[exchangeType] || 'text-slate-400',
    bg: exchangeBgColors[exchangeType] || 'bg-slate-500/10 border-slate-500/20',
    label:
      exchangeType === 'cross-project'
        ? 'Cross-Project Link'
        : exchangeType === 'technosphere'
          ? 'Technosphere Input'
          : exchangeType === 'biosphere'
            ? 'Biosphere Flow'
            : exchangeType === 'production'
              ? 'Production Output'
              : 'Exchange',
  }
  const Icon = config.icon

  const ecoqueryUrl = exchange.ecoquery_url || null

  return (
    <div className="p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center border ${config.bg}`}>
          <Icon className={`w-4 h-4 ${config.color}`} />
        </div>
        <span className={`text-xs font-medium uppercase tracking-wide ${config.color}`}>
          {config.label}
        </span>
      </div>

      {/* Name */}
      <h3 className="text-sm font-semibold text-white leading-snug pr-8">{exchange.name}</h3>

      {/* Amount — prominent */}
      <div className="flex items-baseline gap-2 py-1">
        <span
          className={`text-xl font-bold tabular-nums ${exchange.amount < 0 ? 'text-red-300' : 'text-white'}`}
        >
          {formatAmount(exchange.amount)}
        </span>
        <span className="text-sm text-slate-400">{exchange.unit}</span>
      </div>

      {/* Parent activity */}
      {parentActivity && (
        <div className="flex items-start gap-2 py-1.5 px-2.5 rounded-lg bg-indigo-500/5 border border-indigo-500/10">
          <Layers className="w-3.5 h-3.5 text-indigo-400 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-[10px] text-indigo-400/70 uppercase tracking-wider">
              Parent Activity
            </p>
            <p className="text-xs text-indigo-200 leading-snug">{parentActivity.name}</p>
            <p className="text-[10px] text-slate-500">
              {parentActivity.location} · {parentActivity.unit}
            </p>
          </div>
        </div>
      )}

      {/* Source database */}
      {exchange.input_database && (
        <div className="pt-1 border-t border-slate-800">
          <DetailRow icon={Database} label="Source Database" value={exchange.input_database} />
        </div>
      )}

      {/* Input activity code */}
      {exchange.input_activity_code && (
        <DetailRow
          icon={Hash}
          label="Input Activity Code"
          value={exchange.input_activity_code}
          mono
          copyable
        />
      )}

      {/* Biosphere flow code */}
      {exchange.input_biosphere_code && (
        <DetailRow
          icon={Hash}
          label="Biosphere Flow Code"
          value={exchange.input_biosphere_code}
          mono
          copyable
        />
      )}

      {/* Cross-project source */}
      {exchangeType === 'cross-project' && exchange.input_database?.startsWith('project:') && (
        <div className="flex items-start gap-2 py-1.5 px-2.5 rounded-lg bg-pink-500/5 border border-pink-500/10">
          <Link2 className="w-3.5 h-3.5 text-pink-400 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-[10px] text-pink-400/70 uppercase tracking-wider">
              Cross-Project Reference
            </p>
            <p className="text-xs text-pink-200 font-mono break-all">
              {exchange.input_database.replace('project:', '')}
            </p>
          </div>
        </div>
      )}

      {/* Ecoquery link */}
      {ecoqueryUrl && (
        <div className="pt-1 border-t border-slate-800">
          <a
            href={ecoqueryUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            View on ecoquery.ecoinvent.org
          </a>
        </div>
      )}

      {/* Timestamp */}
      {exchange.created_at && (
        <div className="flex items-center gap-1.5 text-[10px] text-slate-600">
          <Clock className="w-3 h-3" />
          Created{' '}
          {new Date(exchange.created_at).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      )}
    </div>
  )
}
